import { ApolloQueryResult } from '@apollo/client';
import {
  Accordion,
  Alert,
  Button,
  Divider,
  Flex,
  Progress,
  Stack,
  Text,
  TextArea,
  Truncate,
  notify,
} from '@eleveo/components-react';
import { ChatIcon, CheckmarkIcon, CrossIcon, QueueIcon, SwitchPersonIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { getLocalTimeZone, now, parseAbsolute, toCalendarDateTime } from '@internationalized/date';
import clsx from 'clsx';
import { pathOr } from 'ramda';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../app/AuthorizationProvider';
import { RequestStatusComponent } from '../../components/AgentsRequests';
import { DateFormatter } from '../../components/DateFormatter';
import { Sidebar, SidebarContent, SidebarPanel, SidebarPart } from '../../components/LayoutNext/LayoutNext';
import { Center } from '../../components/LayoutNext/components/Center';
import { AgentShiftTile } from '../../components/MySchedule/AgentShiftTile';
import { max255chars } from '../../components/MySchedule/validations';
import { REQUEST_STATE_PROPS } from '../../config/agentsRequests';
import {
  GetSupervisorShiftSwapRequestQuery,
  Queue,
  RequestState,
  RequestSubtype,
  ShiftSwapRequest,
  ShiftSwapRequestAction,
  ShiftSwapRequestState,
  useGetScheduleElementsQuery,
  useGetSupervisorShiftSwapRequestQuery,
} from '../../graphql/components';
import { formatNameOfPerson } from '../../utils/helpers';

type RequestsSidebarShiftSwapDetailProps = {
  handleCancel: () => void;
  handleResolve: (
    fromState: RequestState,
    action: ShiftSwapRequestAction.Approve | ShiftSwapRequestAction.Decline,
    refetchShiftSwapRequestQuery: () => Promise<ApolloQueryResult<GetSupervisorShiftSwapRequestQuery>>,
    message?: string
  ) => Promise<void>;
  requestId: string;
  queues: Record<string, Queue>;
};

export const RequestsSidebarShiftSwapDetail: React.FC<RequestsSidebarShiftSwapDetailProps> = ({
  handleCancel,
  handleResolve,
  requestId,
  queues,
}) => {
  const { t } = useTranslation();

  const { userId, timezone } = useAuth();

  const [messageForRequester, setMessageForRequester] = useState<string | undefined>(undefined);
  const [requestInPastAlert, setRequestInPastAlert] = useState(false);

  const messageForRequesterErrorMessage = useMemo(() => {
    const errorMessage = max255chars(messageForRequester);

    if (!errorMessage) {
      return undefined;
    }

    return t(errorMessage, { characters: 255 });
  }, [messageForRequester, t]);

  const checkIfRequestIsInPast = (requestToValidate: ShiftSwapRequest) => {
    if (!requestToValidate) {
      return false;
    }

    const recipientFrom = requestToValidate.recipientShift.dateRange.from;
    const requesterFrom = requestToValidate.requesterShift.dateRange.from;

    const currentDateTime = toCalendarDateTime(now(getLocalTimeZone()));
    // Check if either the 'from' date of recipient's or requester's Shift is before current date-time
    return (
      currentDateTime.compare(parseAbsolute(recipientFrom, timezone)) > 0 ||
      currentDateTime.compare(parseAbsolute(requesterFrom, timezone)) > 0
    );
  };

  const {
    data: shiftSwapRequestData,
    loading: shiftSwapRequestLoading,
    error: shiftSwapRequestError,
    refetch: refetchShiftSwapRequestQuery,
  } = useGetSupervisorShiftSwapRequestQuery({
    fetchPolicy: 'network-only',
    variables: {
      shiftSwapRequestId: requestId,
      supervisorId: userId,
    },
    notifyOnNetworkStatusChange: true, // Needed to run `onCompleted` after `refetch()`
    onCompleted: ({ supervisorShiftSwapRequest }) => {
      // When no request found, BE replies with 403 which is not internally treated as error
      if (!supervisorShiftSwapRequest) {
        handleCancel();
      } else setRequestInPastAlert(checkIfRequestIsInPast(supervisorShiftSwapRequest));
    },
  });

  const {
    data: { scheduleElements } = { scheduleElements: [] },
    loading: scheduleElementsDataLoading,
    error: scheduleElementsDataError,
  } = useGetScheduleElementsQuery();

  const loading = shiftSwapRequestLoading || scheduleElementsDataLoading;
  const error = shiftSwapRequestError || scheduleElementsDataError;

  const request = shiftSwapRequestData?.supervisorShiftSwapRequest;

  const isPendingApprovalState = request?.state === ShiftSwapRequestState.PendingApproval;
  const isApprovedState = request?.state === ShiftSwapRequestState.Approved;
  const isDeclinedState = request?.state === ShiftSwapRequestState.Declined;

  const [firstAudit, ...restOfAudits] = request?.audits ?? [];

  const skipRendering = (loading && !request) || error;

  const checkValidationBeforeResolveAction = async (
    action: ShiftSwapRequestAction.Approve | ShiftSwapRequestAction.Decline
  ) => {
    if (messageForRequesterErrorMessage || !request) {
      return;
    }

    // Alert was not present at the time of Approve action but may be in past now
    if (action === ShiftSwapRequestAction.Approve && !requestInPastAlert && checkIfRequestIsInPast(request)) {
      notify.warning(`${t('schedule.request.requestInPast')} ${t('schedule.request.requestInPastRepeat')}`);
      setRequestInPastAlert(true);

      return;
    }

    try {
      await handleResolve(
        request.state as unknown as RequestState,
        action,
        refetchShiftSwapRequestQuery,
        messageForRequester
      );

      // clean-up message value after decline/approval
      setMessageForRequester(undefined);
    } catch (err) {
      // `customFetch()` will display error message

      const errorCode = pathOr(undefined, ['networkError', 'result', 'errorCode'], err);

      // there could be various state inconsistencies, however the message should be purged for all of them
      if (errorCode?.match(/fe.sch.shiftswap.request.state.transition..*.unsupported/)) {
        setMessageForRequester(undefined);
      }
    }
  };

  const requesterQueueItems = useMemo(() => {
    const filteredQueues =
      request?.requesterShift.agent.queueIds.map((queueId) => queues[queueId]).filter(Boolean) ?? [];

    return [
      {
        id: 'requester-queueIds',
        title: `${t('queue.queues')} - ${formatNameOfPerson(
          request?.requesterShift.agent.firstName ?? '',
          request?.requesterShift?.agent?.lastName ?? ''
        )}`,
        icon: <QueueIcon />,
        content:
          filteredQueues.length > 0 ? (
            <Stack direction="vertical">
              {filteredQueues.map(({ name, id }) => (
                <Text className="font-bold break-words overflow-auto" key={id}>
                  {name}
                </Text>
              ))}
            </Stack>
          ) : (
            <Flex column alignItems="center">
              <Text>{t('queue.noQueues')}</Text>
            </Flex>
          ),
      },
    ];
  }, [request, queues, t]);

  const recipientQueueItems = useMemo(() => {
    const filteredQueues =
      request?.recipientShift.agent.queueIds.map((queueId) => queues[queueId]).filter(Boolean) ?? [];

    return [
      {
        id: 'recipient-queueIds',
        title: `${t('queue.queues')} - ${formatNameOfPerson(
          request?.recipientShift.agent.firstName ?? '',
          request?.recipientShift?.agent?.lastName ?? ''
        )}`,
        icon: <QueueIcon />,
        content:
          filteredQueues.length > 0 ? (
            <Stack direction="vertical">
              {filteredQueues.map(({ name, id }) => (
                <Text className="font-bold break-words overflow-auto" key={id}>
                  {name}
                </Text>
              ))}
            </Stack>
          ) : (
            <Flex column alignItems="center">
              <Text>{t('queue.noQueues')}</Text>
            </Flex>
          ),
      },
    ];
  }, [request, queues, t]);

  return (
    <Sidebar onClose={handleCancel} title={t('schedule.request.requestDetail')}>
      <>
        {loading && (
          <Center className="h-screen">
            <Progress />
          </Center>
        )}

        {!skipRendering && request && (
          <>
            {isPendingApprovalState && (
              <SidebarPanel>
                <Stack className="w-full">
                  <Button
                    className="mr-15"
                    color="error"
                    onClick={() => checkValidationBeforeResolveAction(ShiftSwapRequestAction.Decline)}
                    testId="requests:request-sidebar:button-decline"
                  >
                    <CrossIcon />
                    {t('layout.action.decline')}
                  </Button>

                  <Button
                    color="success"
                    onClick={() => checkValidationBeforeResolveAction(ShiftSwapRequestAction.Approve)}
                    testId="requests:request-sidebar:button-approve"
                    type="button"
                  >
                    <CheckmarkIcon />
                    {t('layout.action.approve')}
                  </Button>
                </Stack>
              </SidebarPanel>
            )}

            <SidebarContent>
              <SidebarPart>
                <div className="px-20 pt-10 pb-15">
                  {requestInPastAlert && isPendingApprovalState && (
                    <Alert color="warning" variant="outline" className="w-full mb-15">
                      {t('schedule.request.requestInPast')}
                    </Alert>
                  )}

                  <Flex className="w-full" column>
                    <Flex alignItems="baseline" justifyContent="between" className="w-full mb-10" gap={[10, 0]} wrap>
                      <Flex alignItems="center" className="max-w-full" wrap gap={[10, 0]}>
                        <span className="text-sm">{t('form.status')}:</span>
                        <RequestStatusComponent
                          status={request?.state as unknown as RequestState}
                          testId="requests:request-sidebar:shift-swap-request-status"
                        />
                      </Flex>
                      <Flex
                        justifyContent="end"
                        className="text-right text-sm flex-1 wrapper__tiny-column-gap"
                        testId="requests:request-sidebar:shift-swap-request-createdAt-timestamp"
                        wrap
                      >
                        {t('form.created')}:
                        <DateFormatter
                          className="font-bold"
                          options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                        >
                          {new Date(request?.createdAt)}
                        </DateFormatter>
                      </Flex>
                    </Flex>
                  </Flex>

                  <span className="mr-auto text-xs py-10">{t('schedule.request.offeredShift')}:</span>

                  <AgentShiftTile
                    className="shift-swap-request__agent-shift-tile"
                    {...request.requesterShift}
                    scheduleElements={scheduleElements}
                    shiftSubtype={RequestSubtype.Requester}
                    shiftSwapRequestId={request.id}
                    isSelected={false}
                    isSupervisor
                    testIdSlug="requests:request-sidebar:shift-swap-request:requester"
                    queues={queues}
                  />

                  {firstAudit.message && (
                    <>
                      <Stack
                        className="w-full"
                        direction="vertical"
                        spacing={0}
                        testId="requests:request-sidebar:shift-swap-request-audit-log-requester"
                      >
                        <Flex
                          className="mt-10"
                          testId="requests:request-sidebar:shift-swap-request-audit-log:requester-message"
                        >
                          <ChatIcon className="text-blue-700 text-lg" />
                          <span className="ml-10 break-words overflow-auto">{firstAudit.message}</span>
                        </Flex>
                      </Stack>

                      <Divider className="mt-10" />
                    </>
                  )}
                </div>

                <Accordion
                  expansionMode="single"
                  items={requesterQueueItems}
                  testId="requests:request-sidebar:requester-queues"
                >
                  {(item) => <div className="px-20 pt-10 pb-10">{item.content}</div>}
                </Accordion>

                <Stack className="w-full pt-10 -mb-15 px-20 pb-15">
                  <Divider className="flex-1 my-auto" />
                  {isApprovedState ? (
                    <CheckmarkIcon className="text-2xl" style={{ color: 'green' }} />
                  ) : (
                    <SwitchPersonIcon className={clsx('text-2xl', { 'text-grey-500': isDeclinedState })} />
                  )}
                  <Divider className="flex-1 my-auto" />
                </Stack>

                <div className="px-20 pt-10 pb-15">
                  <span className="mr-auto text-xs pb-10">{t('schedule.request.requiredShift')}:</span>

                  <AgentShiftTile
                    className="shift-swap-request__agent-shift-tile"
                    {...request.recipientShift}
                    isSelected={false}
                    isSupervisor
                    scheduleElements={scheduleElements}
                    shiftSubtype={RequestSubtype.Recipient}
                    shiftSwapRequestId={request.id}
                    testIdSlug="requests:request-sidebar:shift-swap-request:recipient"
                    queues={queues}
                  />
                </div>

                <Accordion
                  expansionMode="single"
                  items={recipientQueueItems}
                  testId="requests:request-sidebar:recipient-queues"
                >
                  {(item) => <div className="px-20 pt-10 pb-15">{item.content}</div>}
                </Accordion>

                <div className="px-20 pt-10 pb-15">
                  {/* Audits information part */}
                  {restOfAudits.map(({ message: auditMessage, toState, who, when }, index) => {
                    return (
                      <React.Fragment key={`${toState}-${when}`}>
                        <Stack
                          className="w-full"
                          direction="vertical"
                          spacing={0}
                          testId={`requests:request-sidebar:request-audit-log-${index}`}
                        >
                          <Stack className="mt-10 text-sm">
                            <Truncate>
                              <span data-test-id={`requests:request-sidebar:request-audit-log:to-state-${index}`}>
                                {t(REQUEST_STATE_PROPS[toState].nameKey)}:{' '}
                              </span>
                              <span
                                className="font-bold"
                                data-test-id={`requests:request-sidebar:request-audit-log:person-${index}`}
                              >
                                {formatNameOfPerson(who.firstName, who.lastName)}
                              </span>
                            </Truncate>
                            <DateFormatter
                              className="flex-none font-bold ml-auto"
                              options={{ dateStyle: 'short', timeStyle: 'short', timeZone: timezone }}
                            >
                              {new Date(when)}
                            </DateFormatter>
                          </Stack>

                          {auditMessage && (
                            <Flex
                              className="mt-10"
                              testId={`requests:request-sidebar:request-audit-log:message-${index}`}
                            >
                              <ChatIcon className="text-blue-700 text-lg" />
                              <span className="ml-10 break-words overflow-auto">{auditMessage}</span>
                            </Flex>
                          )}
                        </Stack>

                        <Divider className="mt-10" />
                      </React.Fragment>
                    );
                  })}

                  {isPendingApprovalState && (
                    <TextArea
                      className="w-full mt-15"
                      errorMessage={messageForRequesterErrorMessage}
                      label={t('schedule.request.message')}
                      onChange={(message) => setMessageForRequester(message.length ? message : undefined)}
                      testId="requests:request-sidebar:supervisor:message"
                      validationState={messageForRequesterErrorMessage ? 'invalid' : undefined}
                      value={messageForRequester ?? ''}
                    />
                  )}
                </div>
              </SidebarPart>
            </SidebarContent>
          </>
        )}
      </>
    </Sidebar>
  );
};
